/**
 * OrbSearch — Step 7 basic keyword search.
 *
 * Label-only substring match against the 10 orb labels. No NLP, no
 * semantic search, no ranking. Deliberately trivial — replaced in
 * Phase 6/10 by real intent parsing.
 */
import React, { useState, useEffect } from "react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/auth";

export default function OrbSearch({ onOpenOrb }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    let cancel = false;
    api
      .get(`/northstar/search?q=${encodeURIComponent(q)}`)
      .then((r) => {
        if (cancel) return;
        setResults(r.data.results || []);
        setOpen(true);
      })
      .catch(() => {
        if (cancel) return;
        setResults([]);
        setOpen(true);
      });
    return () => {
      cancel = true;
    };
  }, [q]);

  const activate = (orb) => {
    setOpen(false);
    setQ("");
    if (onOpenOrb) return onOpenOrb(orb);
    if (orb.connected && orb.route) navigate(orb.route);
  };

  return (
    <div className="relative w-full max-w-md">
      <div className="relative">
        <Search className="w-4 h-4 text-white/50 absolute left-4 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q.trim() && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Search your Northstar…"
          aria-label="Search Northstar orbs"
          data-testid="northstar-search-input"
          className="w-full pl-11 pr-4 py-2.5 rounded-full bg-white/5 border border-white/15 text-sm text-white placeholder:text-white/40
                     focus:outline-none focus:border-[#7ED493]/60 focus:bg-white/10 backdrop-blur"
        />
      </div>
      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-2 rounded-2xl bg-[#0B132B]/95 border border-white/15 backdrop-blur-md overflow-hidden z-30"
          data-testid="northstar-search-results"
        >
          {results.length === 0 && (
            <div className="px-4 py-3 text-sm text-white/60" data-testid="northstar-search-empty">
              No orb matches &ldquo;{q}&rdquo;.
            </div>
          )}
          {results.map((orb) => (
            <button
              key={orb.key}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => activate(orb)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 text-left"
              data-testid={`northstar-search-result-${orb.key}`}
            >
              <span className="text-white text-sm font-semibold">{orb.label}</span>
              <span
                className={`text-[10px] uppercase tracking-[0.2em] ${
                  orb.connected ? "text-[#7ED493]" : "text-white/40"
                }`}
              >
                {orb.connected ? "Open" : "Coming soon"}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
